'use strict';

/**
 * Deterministic graph layout and topology helpers for the **PipelineStatus** DAG (React Flow view).
 * Positions follow **longest-path layering** from `dependsOn`, laid out **left → right**:
 * roots (no in-graph parents) sit in the leftmost column; nodes that still have downstream work use their depth column;
 * **leaf** stages (nothing depends on them) are grouped in the **rightmost** column so all pipeline exits align.
 * Within a column, nodes are grouped by parent and centered on that parent's row so aligned edges stay straight.
 * Edges are derived only from `dependsOn`.
 *
 * @typedef {import('../pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage
 */

/** Node card width; column spacing uses this plus the horizontal gap. */
export const LAYOUT_NODE_WIDTH = 268;

export const LAYOUT_HORIZONTAL_GAP = 64;

/** Horizontal distance between layer columns. */
const LAYOUT_COLUMN_STEP_X = LAYOUT_NODE_WIDTH + LAYOUT_HORIZONTAL_GAP;

/** Vertical spacing between nodes stacked in the same column. */
export const LAYOUT_ROW_STEP_Y = 132;

export const PIPELINE_STATUS_STAGE_NODE_TYPE = 'pipelineStatusStage';

/**
 * Optional manual position overrides (applied after layer layout). Prefer adjusting the graph definition order;
 * use overrides only for fine-tuning coordinates.
 *
 * @type {Readonly<Record<string, { x: number, y: number }>>}
 */
export const PIPELINE_STATUS_GRAPH_NODE_LAYOUT = Object.freeze({});

/**
 * Longest-path depth from roots: `depth(node) = 0` if no in-graph parents, else `1 + max(depth(parent))`.
 * Leaf nodes are assigned the maximum depth column so all pipeline exits align on the right.
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {Map<string, { x: number, y: number }>}
 */
export function computePipelineStatusLayerPositions(stages) {
	/** @type {Map<string, { x: number, y: number }>} */
	const pos = new Map();
	if (!stages.length) return pos;

	const idSet = new Set(stages.map((s) => s.id));
	const orderIndex = new Map(stages.map((s, i) => [s.id, i]));
	const parentsOf = new Map(
		stages.map((s) => [s.id, (s.dependsOn || []).filter((d) => idSet.has(d))]),
	);
	/** @type {Map<string, number>} */
	const depthMemo = new Map();
	/** @param {string} id */
	function longestDepth(id) {
		if (depthMemo.has(id)) return /** @type {number} */ (depthMemo.get(id));
		const parents = parentsOf.get(id) || [];
		let d = 0;
		if (parents.length) d = 1 + Math.max(...parents.map(longestDepth));
		depthMemo.set(id, d);
		return d;
	}
	for (const s of stages) longestDepth(s.id);

	const maxLongestDepth = Math.max(0, ...[...depthMemo.values()]);
	const { hasOutgoing } = getPipelineStatusGraphPortSets(stages);

	/** @type {Map<number, string[]>} */
	const byColumn = new Map();
	for (const s of stages) {
		const d = longestDepth(s.id);
		const col = hasOutgoing.has(s.id) ? d : maxLongestDepth;
		if (!byColumn.has(col)) byColumn.set(col, []);
		byColumn.get(col).push(s.id);
	}
	for (const ids of byColumn.values()) {
		ids.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
	}

	const maxCol = byColumn.size ? Math.max(...byColumn.keys()) : 0;
	for (let c = 0; c <= maxCol; c += 1) {
		assignColumnPositions(byColumn.get(c) || [], c * LAYOUT_COLUMN_STEP_X, parentsOf, pos, orderIndex);
	}

	for (const [id, override] of Object.entries(PIPELINE_STATUS_GRAPH_NODE_LAYOUT)) {
		const cur = pos.get(id);
		if (cur) pos.set(id, { x: override.x, y: override.y });
	}

	return pos;
}

/**
 * @param {string[]} ids
 * @param {number} colX
 * @param {Map<string, string[]>} parentsOf
 * @param {Map<string, { x: number, y: number }>} pos
 * @param {Map<string, number>} orderIndex
 */
function assignColumnPositions(ids, colX, parentsOf, pos, orderIndex) {
	/** @type {Map<string, string[]>} */
	const idsByParent = new Map();
	for (const id of ids) {
		const parents = parentsOf.get(id) || [];
		const key = parents[0] || '';
		if (!idsByParent.has(key)) idsByParent.set(key, []);
		idsByParent.get(key).push(id);
	}
	const groups = [...idsByParent.entries()].map(([key, groupIds]) => {
		const parentY = key && pos.has(key) ? pos.get(key).y : 0;
		return {
			ids: groupIds,
			mass: groupIds.length,
			ys: ySlotsAround(parentY, groupIds.length),
			order: orderIndex.get(groupIds[0]) ?? 0,
		};
	});
	groups.sort((a, b) => a.order - b.order);
	separateOverlappingGroups(groups);
	for (const g of groups) {
		for (let i = 0; i < g.ids.length; i += 1) {
			pos.set(g.ids[i], { x: colX, y: g.ys[i] });
		}
	}
}

/** @param {number} parentY @param {number} n @returns {number[]} */
function ySlotsAround(parentY, n) {
	if (n <= 0) return [];
	if (n === 1) return [parentY];
	const above = Math.floor((n - 1) / 2);
	const startY = parentY - above * LAYOUT_ROW_STEP_Y;
	return Array.from({ length: n }, (_, i) => startY + i * LAYOUT_ROW_STEP_Y);
}

/** @param {{ ys: number[], mass: number }[]} groups */
function separateOverlappingGroups(groups) {
	if (groups.length < 2) return;
	for (let pass = 0; pass < 16; pass += 1) {
		let moved = false;
		for (let i = 0; i < groups.length; i += 1) {
			for (let j = i + 1; j < groups.length; j += 1) {
				const a = groups[i];
				const b = groups[j];
				if (!groupsNeedSeparation(a.ys, b.ys)) continue;
				const lighter = a.mass <= b.mass ? a : b;
				const heavier = lighter === a ? b : a;
				shiftGroupOff(lighter, heavier);
				moved = true;
			}
		}
		if (!moved) return;
	}
}

/** @param {number[]} ysA @param {number[]} ysB */
function groupsNeedSeparation(ysA, ysB) {
	const aMin = Math.min(...ysA);
	const aMax = Math.max(...ysA);
	const bMin = Math.min(...ysB);
	const bMax = Math.max(...ysB);
	if (aMax < bMin) return bMin - aMax < LAYOUT_ROW_STEP_Y;
	if (bMax < aMin) return aMin - bMax < LAYOUT_ROW_STEP_Y;
	return true;
}

/**
 * @param {{ ys: number[] }} lighter
 * @param {{ ys: number[] }} heavier
 */
function shiftGroupOff(lighter, heavier) {
	const lMin = Math.min(...lighter.ys);
	const lMax = Math.max(...lighter.ys);
	const hMin = Math.min(...heavier.ys);
	const hMax = Math.max(...heavier.ys);
	const lCenter = (lMin + lMax) / 2;
	const hCenter = (hMin + hMax) / 2;
	if (lCenter <= hCenter) {
		const d = hMin - LAYOUT_ROW_STEP_Y - lMax;
		lighter.ys = lighter.ys.map((y) => y + d);
	} else {
		const d = hMax + LAYOUT_ROW_STEP_Y - lMin;
		lighter.ys = lighter.ys.map((y) => y + d);
	}
}

/**
 * Which nodes participate in at least one rendered edge (for React Flow handle visibility).
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {{ hasIncoming: Set<string>, hasOutgoing: Set<string> }}
 */
export function getPipelineStatusGraphPortSets(stages) {
	const idSet = new Set(stages.map((s) => s.id));
	const hasIncoming = new Set();
	const hasOutgoing = new Set();
	for (const s of stages) {
		for (const d of s.dependsOn || []) {
			if (!idSet.has(d) || d === s.id) continue;
			hasIncoming.add(s.id);
			hasOutgoing.add(d);
		}
	}
	return { hasIncoming, hasOutgoing };
}

/**
 * @param {string} stageId
 * @returns {{ x: number, y: number }}
 * @deprecated Positions come from {@link computePipelineStatusLayerPositions} via {@link mapPipelineStatusStagesToGraphNodes}.
 */
export function getPipelineStatusGraphNodePosition(stageId) {
	const o = PIPELINE_STATUS_GRAPH_NODE_LAYOUT[stageId];
	if (o) return { ...o };
	return { x: 0, y: 0 };
}

/**
 * @param {PipelineStatusStage[]} stages
 * @returns {{ id: string, type: string, position: { x: number, y: number }, data: { stage: PipelineStatusStage, graphHints: { hasIncoming: boolean, hasOutgoing: boolean } } }[]}
 */
export function mapPipelineStatusStagesToGraphNodes(stages) {
	const positions = computePipelineStatusLayerPositions(stages);
	const { hasIncoming, hasOutgoing } = getPipelineStatusGraphPortSets(stages);
	return stages.map((stage) => ({
		id: stage.id,
		type: PIPELINE_STATUS_STAGE_NODE_TYPE,
		position: positions.get(stage.id) ?? { x: 0, y: 0 },
		data: {
			stage,
			graphHints: {
				hasIncoming: hasIncoming.has(stage.id),
				hasOutgoing: hasOutgoing.has(stage.id),
			},
		},
	}));
}

/**
 * Directed edge dep -> stage for each entry in `stage.dependsOn`.
 * Skips unknown ids, self-loops, duplicates, and any edge that would create a directed cycle.
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {{ id: string, source: string, target: string }[]}
 */
export function buildPipelineStatusGraphEdgesFromDependsOn(stages) {
	const idSet = new Set(stages.map((s) => s.id));
	/** @type {{ id: string, source: string, target: string }[]} */
	const tentative = [];
	const seen = new Set();
	for (const s of stages) {
		for (const dep of s.dependsOn || []) {
			if (!idSet.has(dep) || dep === s.id) continue;
			const id = `e:${dep}->${s.id}`;
			if (seen.has(id)) continue;
			seen.add(id);
			tentative.push({ id, source: dep, target: s.id });
		}
	}
	return filterEdgesPreservingDAG(tentative);
}

/**
 * @param {{ id: string, source: string, target: string }[]} edges
 * @returns {{ id: string, source: string, target: string }[]}
 */
function filterEdgesPreservingDAG(edges) {
	/** @type {{ id: string, source: string, target: string }[]} */
	const kept = [];
	for (const e of edges) {
		if (!wouldCreateCycle(kept, e.source, e.target)) kept.push(e);
	}
	return kept;
}

/**
 * Adding `source -> target` introduces a directed cycle iff a path already exists from `target` to `source`.
 * @param {{ source: string, target: string }[]} keptEdges
 * @param {string} source
 * @param {string} target
 */
function wouldCreateCycle(keptEdges, source, target) {
	/** @type {Map<string, string[]>} */
	const adj = new Map();
	for (const e of keptEdges) {
		if (!adj.has(e.source)) adj.set(e.source, []);
		adj.get(e.source).push(e.target);
	}
	return pathExistsInAdjacency(adj, target, source);
}

/**
 * @param {Map<string, string[]>} adj
 * @param {string} from
 * @param {string} to
 */
function pathExistsInAdjacency(adj, from, to) {
	const stack = [from];
	const seen = new Set([from]);
	while (stack.length) {
		const u = stack.pop();
		if (u === to) return true;
		for (const v of adj.get(u) || []) {
			if (!seen.has(v)) {
				seen.add(v);
				stack.push(v);
			}
		}
	}
	return false;
}
